import { tournamentSummary, type Fixture } from "../calendar/competitions";
import { buildReport, lineFor, playedIn, resultFor, type MatchReport } from "../match/report";
import { createRuntime, restoreRuntime, type MatchRuntime, type RuntimeSave } from "../match/runtime";
import type { Catalog } from "../tactics/catalog";
import type { PacingConfig } from "../tactics/recognition";
import { isPoolPlayer } from "../roster/roster";
import type { MatchConfig } from "../sim/engine";
import { U11_9V9 } from "../sim/rules";
import { hashSeed } from "../sim/rng";
import { applyEffect, type Effect } from "../story/consequences";
import { appendEntry } from "../story/ledger";
import { LESSON_FACTS, lessonById, lessonFacts } from "../story/lesson";
import { ROLE_BY_NUMBER } from "../sim/types";
import { HOME_REPORTS_FACT } from "../training/homeSkill";
import { FRIEND_ID, PLAYER_ID, matchSquads, playerClubId, recordMatch, storyContext, touch, type CampaignState } from "./campaign";
import { SCENES } from "./season";

/**
 * Campaign matches (spec §10, §12): the fixture's real rosters go into the sim, the user's locked
 * position is the controlled player, and the finished match leaves a `MatchReport`. Everything a
 * postgame scene may say is derived here as facts from that report — never from the live state,
 * and never about a person the report does not list as having played (spec §5).
 */

export function fixtureById(c: CampaignState, id: string): Fixture {
  const f = c.competitions.fixtures.find((x) => x.id === id);
  if (!f) throw new Error(`unknown fixture ${id}`);
  return f;
}

export function matchSeed(c: CampaignState, fixture: Fixture): number {
  return hashSeed(`${c.seed}:${fixture.id}`);
}

/** Sim configuration for a fixture the user's club plays; the user is always controlled and always starts. */
export function campaignMatchConfig(c: CampaignState, fixture: Fixture): MatchConfig {
  const mine = playerClubId(c);
  if (!mine) throw new Error("player has no club");
  const side = fixture.homeClubId === mine ? "home" : fixture.awayClubId === mine ? "away" : null;
  if (!side) throw new Error(`fixture ${fixture.id} does not involve ${mine}`);
  const seed = matchSeed(c, fixture);
  const squads = matchSquads(c, fixture, seed);
  const club = (id: string) => c.roster.clubs.find((k) => k.id === id);
  const home = club(fixture.homeClubId);
  const away = club(fixture.awayClubId);
  const controlledSquad = side === "home" ? squads.home : squads.away;
  if (!controlledSquad.some((p) => p.id === PLAYER_ID)) throw new Error("player not in the squad");
  return {
    matchId: `${c.id}:${fixture.id}`,
    seed,
    rules: U11_9V9,
    home: { side: "home", name: home?.name ?? fixture.homeClubId, shortName: home?.shortName ?? fixture.homeClubId.slice(0, 3).toUpperCase(), squad: squads.home },
    away: { side: "away", name: away?.name ?? fixture.awayClubId, shortName: away?.shortName ?? fixture.awayClubId.slice(0, 3).toUpperCase(), squad: squads.away },
    controlled: { side, playerId: PLAYER_ID },
  };
}

/** Persist the live match into the pending activity so a reload comes back to the same question. */
export function checkpointMatch(c: CampaignState, save: RuntimeSave): boolean {
  if (!c.pending || c.pending.kind !== "match") return false;
  c.pending = { ...c.pending, save };
  touch(c);
  return true;
}

/** The pending fixture's runtime: restored from its checkpoint when one exists, otherwise a fresh deterministic match. */
export function pendingMatchRuntime(c: CampaignState, fixture: Fixture, catalog: Catalog, pacing: PacingConfig): MatchRuntime {
  const p = c.pending;
  if (p?.kind === "match" && p.save) return restoreRuntime(p.save, catalog);
  return createRuntime(campaignMatchConfig(c, fixture), catalog, { pacing });
}

export function reportFromRuntime(runtime: MatchRuntime, fixture: Fixture): MatchReport {
  return buildReport(runtime.state, runtime.session.records, {
    fixtureId: fixture.id,
    homeClubId: fixture.homeClubId,
    awayClubId: fixture.awayClubId,
    isPool: isPoolPlayer,
  });
}

export const MATCH_FACTS = {
  played: "matches_played",
  result: "last_result",
  score: "last_score",
  opponent: "last_opponent",
  kind: "last_match_kind",
  myGoals: "last_my_goals",
  myShots: "last_my_shots",
  myPasses: "last_my_pass_rate",
  friendPlayed: "last_friend_played",
  friendGoals: "last_friend_goals",
  moments: "last_moments",
  strongReads: "last_strong_reads",
  weakReads: "last_weak_reads",
  timeouts: "last_timeouts",
  goodReadPoorExecution: "last_good_read_poor_execution",
  poorReadGoodOutcome: "last_poor_read_good_outcome",
  reads: "last_match_reads",
  weekTrained: "week_trainings_attended",
  weekMissed: "week_trainings_missed",
  weekHome: "week_home_reports",
  /** League matches the player actually played. */
  leaguePlayed: "league_matches_played",
  /** Consecutive league wins (+n) or losses (−n) in matches the player played; a draw resets to 0. */
  streak: "result_streak",
} as const;

/** Pathway evidence (spec §19 "opportunities and pathway access"): what other clubs could notice. */
export const PATHWAY_EVIDENCE = { sharpLeagueReads: 1, tournamentTitle: 4 } as const;

export function nextStreak(previous: number, result: "win" | "draw" | "loss"): number {
  if (result === "draw") return 0;
  if (result === "win") return previous > 0 ? previous + 1 : 1;
  return previous < 0 ? previous - 1 : -1;
}

/** Attendance in the seven days up to and including match day, from the schedule itself. */
export function weekAttendance(c: CampaignState, matchDay: number): { attended: number; missed: number } {
  let attended = 0;
  let missed = 0;
  for (const k of c.schedule.commitments) {
    if (k.kind !== "training" || k.day > matchDay || k.day <= matchDay - 7) continue;
    if (k.status === "attended") attended++;
    else if (k.status === "missed") missed++;
  }
  return { attended, missed };
}

/** `none` when no tactical moment was recorded — then nothing may be claimed about the reads. */
export type MatchReads = "sharp" | "mixed" | "rushed" | "none";

export function matchReads(r: MatchReport): MatchReads {
  const m = r.moments;
  if (m.total === 0) return "none";
  const total = m.total;
  const strong = m.decisions.strong / total;
  const sound = (m.decisions.strong + m.decisions.acceptable) / total;
  return strong >= 0.5 && m.decisions.timeout <= 1 ? "sharp" : sound >= 0.5 ? "mixed" : "rushed";
}

/** Facts derived from the report, for the postgame scenes. Pure. */
export function matchFacts(c: CampaignState, r: MatchReport, fixture: Fixture): Effect[] {
  const mine = playerClubId(c)!;
  const side = r.home.clubId === mine ? "home" : "away";
  const opponent = side === "home" ? r.away : r.home;
  const me = lineFor(r, PLAYER_ID);
  const friendPlayed = playedIn(r, FRIEND_ID);
  const friend = friendPlayed ? lineFor(r, FRIEND_ID) : undefined;
  const num = (id: string): number => (typeof c.story.facts[id] === "number" ? (c.story.facts[id] as number) : 0);
  const played = num(MATCH_FACTS.played);
  const week = weekAttendance(c, c.day);
  const homeReports = num(HOME_REPORTS_FACT);
  const result = resultFor(r, side);
  const reads = matchReads(r);
  const effects: Effect[] = [
    { type: "set_fact", id: MATCH_FACTS.played, value: played + 1 },
    { type: "set_fact", id: MATCH_FACTS.result, value: result },
    { type: "set_fact", id: MATCH_FACTS.score, value: side === "home" ? `${r.score.home}–${r.score.away}` : `${r.score.away}–${r.score.home}` },
    { type: "set_fact", id: MATCH_FACTS.opponent, value: opponent.name },
    { type: "set_fact", id: MATCH_FACTS.kind, value: fixture.kind },
    { type: "set_fact", id: MATCH_FACTS.myGoals, value: me?.goals ?? 0 },
    { type: "set_fact", id: MATCH_FACTS.myShots, value: me?.shots ?? 0 },
    { type: "set_fact", id: MATCH_FACTS.myPasses, value: me && me.passes ? Math.round((100 * me.passesCompleted) / me.passes) : 0 },
    { type: "set_fact", id: MATCH_FACTS.friendPlayed, value: friendPlayed },
    { type: "set_fact", id: MATCH_FACTS.friendGoals, value: friend?.goals ?? 0 },
    { type: "set_fact", id: MATCH_FACTS.moments, value: r.moments.total },
    { type: "set_fact", id: MATCH_FACTS.strongReads, value: r.moments.decisions.strong },
    { type: "set_fact", id: MATCH_FACTS.weakReads, value: r.moments.decisions.weak },
    { type: "set_fact", id: MATCH_FACTS.timeouts, value: r.moments.decisions.timeout },
    { type: "set_fact", id: MATCH_FACTS.goodReadPoorExecution, value: r.moments.goodReadPoorExecution.length },
    { type: "set_fact", id: MATCH_FACTS.poorReadGoodOutcome, value: r.moments.poorReadGoodOutcome.length },
    { type: "set_fact", id: MATCH_FACTS.reads, value: reads },
    { type: "set_fact", id: MATCH_FACTS.weekTrained, value: week.attended },
    { type: "set_fact", id: MATCH_FACTS.weekMissed, value: week.missed },
    { type: "set_fact", id: MATCH_FACTS.weekHome, value: homeReports },
  ];
  if (fixture.kind === "league") {
    effects.push(
      { type: "set_fact", id: MATCH_FACTS.leaguePlayed, value: num(MATCH_FACTS.leaguePlayed) + 1 },
      { type: "set_fact", id: MATCH_FACTS.streak, value: nextStreak(num(MATCH_FACTS.streak), result) },
    );
    if (reads === "sharp") effects.push({ type: "track", track: "pathway", delta: PATHWAY_EVIDENCE.sharpLeagueReads });
  }
  // The parent watched from the touchline: they know the result and what they could see, not the reads.
  for (const id of [MATCH_FACTS.result, MATCH_FACTS.score, MATCH_FACTS.opponent, MATCH_FACTS.myGoals, MATCH_FACTS.friendPlayed]) {
    effects.push({ type: "learn", personId: "parent", factId: id });
  }
  // The coach saw the decisions, and takes the register.
  for (const id of [
    MATCH_FACTS.result,
    MATCH_FACTS.reads,
    MATCH_FACTS.goodReadPoorExecution,
    MATCH_FACTS.poorReadGoodOutcome,
    MATCH_FACTS.myGoals,
    MATCH_FACTS.weekTrained,
    MATCH_FACTS.weekMissed,
  ]) {
    effects.push({ type: "learn", personId: "coach", factId: id });
  }
  // The friend knows what happened only if they were on the pitch.
  if (friendPlayed) {
    for (const id of [MATCH_FACTS.result, MATCH_FACTS.score, MATCH_FACTS.myGoals, MATCH_FACTS.friendGoals]) {
      effects.push({ type: "learn", personId: "friend", factId: id });
    }
  }
  return effects;
}

export type CompleteMatchResult =
  | { ok: true; effects: Effect[]; duplicate: false }
  | { ok: true; effects: []; duplicate: true }
  | { ok: false; reason: string };

export const TOURNAMENT_FACTS = {
  name: "tournament_name",
  game: "tournament_game",
  games: "tournament_games",
  record: "tournament_record",
  /** Whether the next (or just played last) game is a final rather than a placement match. */
  final: "tournament_final",
  outcome: "tournament_outcome",
} as const;

/**
 * Facts about the tournament a fixture belongs to, from the club's tournament fixtures alone. Pure.
 * Returns the postgame scenes for this point of the weekend: between games, the evening in the
 * hotel after the day's last game, or the coach's wrap after the final.
 */
export function tournamentFacts(c: CampaignState, fixture: Fixture): { effects: Effect[]; scenes: string[] } {
  const mine = playerClubId(c)!;
  const t = c.competitions.tournaments.find((x) => x.id === fixture.competitionId);
  const games = c.competitions.fixtures
    .filter((f) => f.competitionId === fixture.competitionId && (f.homeClubId === mine || f.awayClubId === mine))
    .sort((a, b) => a.day - b.day || a.id.localeCompare(b.id));
  const idx = games.findIndex((f) => f.id === fixture.id);
  const s = tournamentSummary(c.competitions, fixture.competitionId, mine);
  const last = idx === games.length - 1;
  const lastToday = last || games[idx + 1]!.day !== fixture.day;
  const effects: Effect[] = [
    { type: "set_fact", id: TOURNAMENT_FACTS.name, value: t?.name ?? fixture.competitionId },
    { type: "set_fact", id: TOURNAMENT_FACTS.game, value: idx + 1 },
    { type: "set_fact", id: TOURNAMENT_FACTS.games, value: games.length },
    { type: "set_fact", id: TOURNAMENT_FACTS.record, value: `${s.won}-${s.drawn}-${s.lost}` },
    { type: "set_fact", id: TOURNAMENT_FACTS.final, value: s.finalIsFinal },
    { type: "set_fact", id: TOURNAMENT_FACTS.outcome, value: s.outcome },
  ];
  for (const id of [TOURNAMENT_FACTS.record, TOURNAMENT_FACTS.final, TOURNAMENT_FACTS.outcome]) {
    effects.push({ type: "learn", personId: "coach", factId: id });
    effects.push({ type: "learn", personId: "friend", factId: id });
    effects.push({ type: "learn", personId: "parent", factId: id });
  }
  if (last && s.outcome === "champions") effects.push({ type: "track", track: "pathway", delta: PATHWAY_EVIDENCE.tournamentTitle });
  const scenes = last ? [SCENES.end, "week.postgame"] : lastToday ? [SCENES.evening] : [SCENES.between];
  return { effects, scenes };
}

/**
 * Persist the report, ingest the fixture result (idempotent), derive facts, and queue the postgame
 * scenes for today. Re-submitting the same match is a no-op. A tournament game gets the weekend's
 * scenes instead of the league Saturday's drive home.
 */
export function completeCampaignMatch(c: CampaignState, report: MatchReport): CompleteMatchResult {
  if (!report.finished) return { ok: false, reason: "match not finished" };
  if (!report.fixtureId) return { ok: false, reason: "campaign match without fixture" };
  const fixture = fixtureById(c, report.fixtureId);
  const before = c.competitions.appliedEventIds.includes(report.eventId);
  const ingest = recordMatch(c, report);
  if (!ingest.ok) return ingest.reason === "duplicate_event" || before ? { ok: true, effects: [], duplicate: true } : { ok: false, reason: ingest.reason };
  const effects = matchFacts(c, report, fixture);
  effects.push({ type: "track", track: "physical", delta: 1 });
  const role = ROLE_BY_NUMBER[c.player.position];
  appendEntry(c.story.ledger, {
    id: report.eventId,
    day: c.day,
    kind: "match",
    source: "soccer_engine",
    payload: {
      eventId: report.eventId,
      fixtureId: fixture.id,
      home: report.home.clubId,
      away: report.away.clubId,
      score: { ...report.score },
      role,
      moments: report.moments.faced.map((m) => m.entryId),
    },
  });
  const lessonId = c.story.facts[LESSON_FACTS.active];
  const lesson = typeof lessonId === "string" ? lessonById(lessonId) : undefined;
  if (lesson) {
    const facedBefore = c.story.facts[LESSON_FACTS.facedTotal];
    effects.push(...lessonFacts(lesson, role, report, typeof facedBefore === "number" ? facedBefore : 0), { type: "clear_fact", id: LESSON_FACTS.active });
  }
  if (fixture.kind === "tournament") {
    const t = tournamentFacts(c, fixture);
    effects.push(...t.effects);
    effects.push({ type: "queue_scene", sceneId: "week.coach_word", onDay: c.day });
    for (const sceneId of t.scenes) effects.push({ type: "queue_scene", sceneId, onDay: c.day });
  } else {
    effects.push({ type: "queue_scene", sceneId: "week.coach_word", onDay: c.day });
    effects.push({ type: "queue_scene", sceneId: "week.postgame", onDay: c.day });
    effects.push({ type: "queue_scene", sceneId: "week.friend_after_match", onDay: c.day });
  }
  const ctx = storyContext(c);
  for (const e of effects) applyEffect(ctx, e);
  touch(c);
  return { ok: true, effects, duplicate: false };
}
